import React, { Fragment, useRef, useState, useMemo, useEffect } from "react";
import swal from "sweetalert";
import { db } from "../../../../firebase";
import {
  doc,
  where,
  query,
  collection,
  and,
  updateDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  getDocs,
  limit,
} from "firebase/firestore";
import { Button, Modal } from "react-bootstrap";
import { useAuth } from "../../../../context/AuthContext";
import PageTitle from "../../../layouts/PageTitle";
import AccessoriesItem from "./AccessoriesItem";
import AccessoriesForm from "./AccessoriesForm";
import BulkStockUpdateModal from "./BulkStockUpdateModal";
import WithdrawModal from "./WithdrawModal";
import {
  accessoryStatusForStock,
  consumeAccessoryFifo,
  createAccessoryWithOpeningLot,
  receiveAccessoryLots,
} from "../../../../utils/accessoryLotInventory";

// Sort accessories to mirror the printed catalog / price-list ordering.
// Brands appear in catalog order (ResMed, Wellell, VentMed, Topson, Philips),
// with any other brand (Apex, BMC, Yuwell, ...) alphabetically after them.
// Within a brand, items follow `catalogOrder` (written onto each doc by
// functions/scripts/backfillAccessoryCatalogOrder.js); items without a
// catalogOrder — variants, duplicates, or newly added items — sort after their
// brand's catalogued items so nothing ever disappears from the list.
const CATALOG_BRAND_RANK = {
  resmed: 1,
  wellell: 2,
  ventmed: 3,
  topson: 4,
  philips: 5,
};

const brandRank = (brand) => {
  const key = String(brand || "").trim().toLowerCase();
  return CATALOG_BRAND_RANK[key] || 99; // unknown brands go after the catalog brands
};

const compareByCatalog = (a, b) => {
  const rankA = brandRank(a.brand);
  const rankB = brandRank(b.brand);
  if (rankA !== rankB) return rankA - rankB;

  // Non-catalog brands (rank 99): keep each brand grouped, alphabetically.
  if (rankA === 99) {
    const brandCmp = String(a.brand || "").localeCompare(String(b.brand || ""));
    if (brandCmp !== 0) return brandCmp;
  }

  // Within a brand: catalog order first, un-catalogued items (Infinity) last.
  const orderA = typeof a.catalogOrder === "number" ? a.catalogOrder : Infinity;
  const orderB = typeof b.catalogOrder === "number" ? b.catalogOrder : Infinity;
  if (orderA !== orderB) return orderA - orderB;

  return String(a.itemName || "").localeCompare(String(b.itemName || ""));
};

const AccessoriesList = () => {
  const { currentUser } = useAuth();
  const canManageCatalogOrder =
    currentUser &&
    Array.isArray(currentUser.roles) &&
    currentUser.roles.some((role) => ["super_admin", "admin"].includes(role));
  const canAdjustStock =
    currentUser &&
    Array.isArray(currentUser.roles) &&
    currentUser.roles.some((role) => ["super_admin", "admin"].includes(role));
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [edit, setEdit] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchItem, setSearchItem] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ทั้งหมด");
  const [statusFilter, setStatusFilter] = useState("ทั้งหมด");
  const searchRef = useRef(null);
  const [allItemsStats, setAllItemsStats] = useState({
    totalCount: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
    byCategory: {},
    outOfStockByCategory: {},
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const itemPerPage = 50;

  const [accessoriesList, setAccessoriesList] = useState([]);
  const [catalogAccessories, setCatalogAccessories] = useState([]);
  const [catalogAccessoriesLoading, setCatalogAccessoriesLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Subscribe to accessories, sort them in catalog order client-side, and
  // compute the summary stats in one place. Sorting happens client-side (rather
  // than via a Firestore orderBy) because catalog order is a custom brand
  // ordering, and because an orderBy would hide any doc missing the sort field.
  useEffect(() => {
    setIsLoading(true);
    setStatsLoading(true);
    setCurrentPage(1);

    const constraints = [];
    if (searchItem !== "") {
      constraints.push(
        where("itemCode", ">=", searchItem),
        where("itemCode", "<=", searchItem + "")
      );
    }
    if (categoryFilter !== "ทั้งหมด") {
      constraints.push(where("category", "==", categoryFilter));
    }
    const accessoriesRef = collection(db, "accessories");
    const queryRef =
      constraints.length > 1
        ? query(accessoriesRef, and(...constraints))
        : constraints.length === 1
        ? query(accessoriesRef, constraints[0])
        : query(accessoriesRef);

    const unsubscribe = onSnapshot(
      queryRef,
      (snapshot) => {
        const items = snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            return {
              ...data,
              id: docSnap.id,
              status: accessoryStatusForStock(
                data.status,
                data.stockQuantity
              ),
            };
          })
          .filter(
            (item) =>
              statusFilter === "ทั้งหมด" || item.status === statusFilter
          )
          .sort(compareByCatalog);

        const stats = {
          totalCount: items.length,
          lowStockCount: 0,
          outOfStockCount: 0,
          byCategory: {},
          outOfStockByCategory: {},
        };

        items.forEach((item) => {
          const stockQty = item.stockQuantity || 0;
          const category = item.category || "Other";

          if (stockQty <= 0) {
            stats.outOfStockCount++;
            stats.outOfStockByCategory[category] =
              (stats.outOfStockByCategory[category] || 0) + 1;
          } else if (item.minimumStock && stockQty <= item.minimumStock) {
            stats.lowStockCount++;
          }

          stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
        });

        setAccessoriesList(items);
        setAllItemsStats(stats);
        setIsLoading(false);
        setStatsLoading(false);
      },
      (error) => {
        console.error("Error fetching accessories:", error);
        setIsLoading(false);
        setStatsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [searchItem, categoryFilter, statusFilter]);

  useEffect(() => {
    if (!showModal || !canManageCatalogOrder) {
      setCatalogAccessoriesLoading(false);
      return undefined;
    }

    setCatalogAccessoriesLoading(true);

    const unsubscribe = onSnapshot(
      collection(db, "accessories"),
      (snapshot) => {
        setCatalogAccessories(
          snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }))
        );
        setCatalogAccessoriesLoading(false);
      },
      (error) => {
        console.error("Error fetching catalog accessory positions:", error);
        setCatalogAccessoriesLoading(false);
      }
    );

    return () => unsubscribe();
  }, [showModal, canManageCatalogOrder]);

  // Client-side pagination over the fully-sorted list.
  const totalItems = accessoriesList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemPerPage));
  const page = Math.min(currentPage, totalPages);
  const pageItems = accessoriesList.slice(
    (page - 1) * itemPerPage,
    page * itemPerPage
  );
  const isStart = page <= 1;
  const isEnd = page >= totalPages;
  const goToPage = (number) =>
    setCurrentPage(Math.min(Math.max(1, number), totalPages));
  const getPrev = () => goToPage(page - 1);
  const getNext = () => goToPage(page + 1);

  const handleOnEdit = (id) => {
    const data = accessoriesList.find((item) => item.id === id);
    setEdit(data);
    setShowModal(true);
  };

  const handleOnHide = () => {
    setShowModal(false);
    setEdit({});
    setCatalogAccessories([]);
    setCatalogAccessoriesLoading(false);
  };

  const handleOnDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this accessory!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const [lots, logs] = await Promise.all([
            getDocs(query(collection(db, "accessories", id, "lots"), limit(1))),
            getDocs(query(collection(db, "stockLogs"), where("itemId", "==", id), limit(1))),
          ]);
          if (!lots.empty || !logs.empty) {
            swal(
              "Cannot delete accessory",
              "This accessory has lot or stock-log history. Set its status to Discontinued instead.",
              "warning"
            );
            return;
          }
          await deleteDoc(doc(db, "accessories", id));
          swal("Poof! Your accessory has been deleted!", {
            icon: "success",
          });
        } catch (error) {
          console.error("Error deleting accessory:", error);
          swal("Error", "Failed to delete accessory", "error");
        }
      } else {
        swal("Cancelled");
      }
    });
  };

  const handleOnSubmit = async (accessoryData) => {
    try {
      const { catalogOrder, ...dataWithoutCatalogOrder } = accessoryData;
      const shouldWriteCatalogOrder =
        canManageCatalogOrder &&
        Object.prototype.hasOwnProperty.call(accessoryData, "catalogOrder");

      if (edit.id) {
        // Update existing accessory
        const updateData = {
          ...dataWithoutCatalogOrder,
          status: accessoryStatusForStock(
            dataWithoutCatalogOrder.status,
            edit.stockQuantity
          ),
          updatedAt: new Date(),
          updatedBy: currentUser.displayName || currentUser.email,
        };
        ["stockQuantity", "lotNo", "incomingDate", "expiryDate", "lotTrackingVersion"].forEach(
          (field) => delete updateData[field]
        );

        if (shouldWriteCatalogOrder) {
          updateData.catalogOrder =
            catalogOrder === null ? deleteField() : catalogOrder;
        }

        await updateDoc(doc(db, "accessories", edit.id), updateData);
        swal("Success", "Accessory updated successfully!", "success");
      } else {
        // Add new accessory
        const newAccessoryData = {
          ...dataWithoutCatalogOrder,
          createdAt: new Date(),
          createdBy: currentUser.displayName || currentUser.email,
          updatedAt: new Date(),
          updatedBy: currentUser.displayName || currentUser.email,
        };

        if (shouldWriteCatalogOrder && catalogOrder !== null) {
          newAccessoryData.catalogOrder = catalogOrder;
        }

        await createAccessoryWithOpeningLot(db, newAccessoryData, currentUser);
        swal("Success", "Accessory added successfully!", "success");
      }
      handleOnHide();
    } catch (error) {
      console.error("Error saving accessory:", error);
      swal("Error", "Failed to save accessory", "error");
    }
  };

  const onSearchChange = () => {
    setSearchItem(searchRef.current.value);
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleSelectAll = () => {
    const pageIds = pageItems.map((item) => item.id);
    const allSelected = pageIds.every((id) => selectedItems.includes(id));
    if (allSelected) {
      setSelectedItems((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedItems((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleBulkStockUpdate = async (stockUpdates) => {
    try {
      const result = await receiveAccessoryLots(db, stockUpdates, currentUser);

      swal(
        "Success",
        `Saved ${stockUpdates.length} items. Initialized ${result.initializedCount}; received stock for ${result.receiptCount}.`,
        "success"
      );
      setSelectedItems([]);
      setShowBulkModal(false);
    } catch (error) {
      console.error("Error updating stock:", error);
      swal("Error", error.message || "Failed to update stock quantities", "error");
    }
  };

  const handleWithdrawStock = async (stockUpdates) => {
    if (!canAdjustStock) {
      swal(
        "Access denied",
        "Only administrators can make immediate stock adjustments.",
        "error"
      );
      return;
    }

    try {
      await Promise.all(stockUpdates.map((update) => consumeAccessoryFifo(db, {
        accessoryId: update.id,
        quantity: update.amountWithdrawn,
        lotId: update.lotId || null,
        type: "adjust",
        notes: update.notes || "Immediate stock withdrawal",
      }, currentUser)));

      swal(
        "Success",
        `Adjusted stock for ${stockUpdates.length} items!`,
        "success"
      );
      setSelectedItems([]);
      setShowWithdrawModal(false);
    } catch (error) {
      console.error("Error adjusting stock:", error);
      swal(
        "Error",
        error.message || "Failed to adjust stock quantities",
        "error"
      );
    }
  };

  // Format out-of-stock by category for display
  const outOfStockByCategory = useMemo(() => {
    return Object.entries(allItemsStats.outOfStockByCategory).sort(([a], [b]) =>
      a.localeCompare(b)
    );
  }, [allItemsStats.outOfStockByCategory]);

  return (
    <Fragment>
      <PageTitle activeMenu="Accessories List" motherMenu="Sales" />

      <Modal
        className="fade bd-example-modal-lg"
        show={showModal}
        onHide={handleOnHide}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>
            {edit.id ? "Edit Accessory" : "Add New Accessory"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AccessoriesForm
            accessory={edit}
            onSubmit={handleOnSubmit}
            accessories={catalogAccessories}
            catalogLoading={catalogAccessoriesLoading}
            canManageCatalogOrder={canManageCatalogOrder}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleOnHide}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <BulkStockUpdateModal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        selectedItems={selectedItems}
        accessories={accessoriesList}
        onSubmit={handleBulkStockUpdate}
      />

      <WithdrawModal
        show={Boolean(canAdjustStock && showWithdrawModal)}
        onHide={() => setShowWithdrawModal(false)}
        selectedItems={selectedItems}
        accessories={accessoriesList}
        onSubmit={handleWithdrawStock}
      />

      {/* Stock Summary Cards - Shows statistics for ALL accessories */}
      <div className="col-xl-12 col-xxl-12 mb-4">
        <div className="row">
          <div className="col-xl-3 col-lg-6 col-sm-6">
            <div className="widget-stat card bg-primary pt-4 pb-4">
              <div className="card-body text-white text-center">
                {statsLoading ? (
                  <>
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mb-0 mt-2">Loading...</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-white">{allItemsStats.totalCount}</h3>
                    <p className="mb-0">Total Items</p>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-lg-6 col-sm-6">
            <div className="widget-stat card bg-warning pt-4 pb-4">
              <div className="card-body text-white text-center">
                {statsLoading ? (
                  <>
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mb-0 mt-2">Loading...</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-white">{allItemsStats.lowStockCount}</h3>
                    <p className="mb-0">Low Stock</p>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-lg-6 col-sm-6">
            <div className="widget-stat card bg-danger pt-4 pb-4">
              <div className="card-body text-white text-center">
                {statsLoading ? (
                  <>
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mb-0 mt-2">Loading...</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-white">{allItemsStats.outOfStockCount}</h3>
                    <p className="mb-0">Out of Stock</p>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-lg-6 col-sm-6">
            <div className="widget-stat card bg-secondary pt-4 pb-4">
              <div
                className="card-body text-white"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                <h4 className="text-center text-white mb-3">
                  Out of Stock by Category
                </h4>
                {statsLoading ? (
                  <div className="text-center">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mb-0 mt-2">
                      <small>Loading...</small>
                    </p>
                  </div>
                ) : outOfStockByCategory.length > 0 ? (
                  outOfStockByCategory.map(([category, count]) => (
                    <div
                      key={category}
                      className="d-flex justify-content-between align-items-center mb-2 px-2"
                    >
                      <span className="text-white">{category}</span>
                      <span className="badge badge-light">{count}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-center">
                    <small className="text-white-50">All items in stock</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="form-head d-flex mb-3 mb-lg-5 align-items-start">
        <Button
          className="btn btn-danger mr-3"
          onClick={() => setShowModal(true)}
          data-toggle="tooltip"
          data-placement="top"
          title="Add New Accessory"
        >
          + Add Accessory
        </Button>

        {selectedItems.length > 0 && (
          <>
            <Button
              className="btn btn-success mr-3"
              onClick={() => setShowBulkModal(true)}
              data-toggle="tooltip"
              data-placement="top"
              title="Add stock for selected items"
            >
              📦 Add Stock ({selectedItems.length})
            </Button>
            {canAdjustStock && (
              <Button
                className="btn btn-warning mr-3"
                onClick={() => setShowWithdrawModal(true)}
                data-toggle="tooltip"
                data-placement="top"
                title="Make an immediate stock adjustment for selected items"
              >
                ⚠️ Stock Adjustment ({selectedItems.length})
              </Button>
            )}
            <Button
              className="btn btn-secondary mr-3"
              onClick={() => setSelectedItems([])}
              size="sm"
            >
              Clear Selection
            </Button>
          </>
        )}

        {/* Filters */}
        <div className="ml-3">
          <select
            className="form-control"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ width: "150px", display: "inline-block" }}
          >
            <option value="ทั้งหมด">All Categories</option>
            <option value="Mask">Mask</option>
            <option value="Tube">Tube</option>
            <option value="Filter">Filter</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="ml-2">
          <select
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: "150px", display: "inline-block" }}
          >
            <option value="ทั้งหมด">All Status</option>
            <option value="Active">Active</option>
            <option value="Discontinued">Discontinued</option>
            <option value="Out of Stock">Out of Stock</option>
          </select>
        </div>

        <div className="input-group search-area ml-auto d-inline-flex">
          <input
            type="text"
            className="form-control"
            placeholder="Search item code..."
            ref={searchRef}
            onChange={onSearchChange}
          />
          <div className="input-group-append">
            <span className="input-group-text c-pointer">
              <i className="flaticon-381-search-2"></i>
            </span>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <AccessoriesItem
          accessories={pageItems}
          onEdit={handleOnEdit}
          onDelete={handleOnDelete}
          selectedItems={selectedItems}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
          totalItems={totalItems}
          totalPages={totalPages}
          currentPage={page}
          itemsPerPage={itemPerPage}
          goToPage={goToPage}
          isStart={isStart}
          isEnd={isEnd}
          getNext={getNext}
          getPrev={getPrev}
        />
      )}
    </Fragment>
  );
};

export default AccessoriesList;
